import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const categoryLinks = [
  { id: "movie", label: "电影", icon: "ri-film-line" },
  { id: "tv", label: "电视剧", icon: "ri-tv-2-line" },
  { id: "web", label: "网络剧", icon: "ri-smartphone-line" },
];

const navItems = [
  { to: "/", label: "首页" },
  { to: "/special", label: "专题合集" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm rounded-md transition-colors cursor-pointer ${
      isActive
        ? "text-primary-700 font-semibold"
        : "text-foreground-700 hover:text-primary-700 hover:bg-primary-50"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/90 backdrop-blur">
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-movie-2-line text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold text-foreground-950">
                妻子的朋友
              </span>
              <span className="text-[10px] tracking-widest text-foreground-400 uppercase">
                Film &amp; Series
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" end className={linkClass}>
              首页
            </NavLink>

            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex items-center gap-1 px-3 py-2 text-sm rounded-md text-foreground-700 hover:text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                分类
                <i
                  className={`ri-arrow-down-s-line transition-transform ${
                    catOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 w-44 rounded-lg border border-background-200 bg-background-50 py-1 animate-scale-in">
                  {categoryLinks.map((c) => (
                    <Link
                      key={c.id}
                      to={`/category/${c.id}`}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={c.icon}></i>
                      </span>
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navItems.slice(1).map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/search"
              className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-full border border-background-300 bg-background-100 text-sm text-foreground-500 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line"></i>
              </span>
              搜索影视
            </Link>
            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 animate-fade-in">
          <NavLink to="/" end className={linkClass}>
            首页
          </NavLink>
          <div className="px-3 pt-3 pb-1 text-xs font-semibold tracking-wider text-foreground-400 uppercase">
            分类
          </div>
          <div className="grid grid-cols-1 gap-1">
            {categoryLinks.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 rounded-md cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={c.icon}></i>
                </span>
                {c.label}
              </Link>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link
              to="/special"
              className="h-10 flex items-center justify-center rounded-md bg-primary-50 text-sm text-primary-700 cursor-pointer"
            >
              专题合集
            </Link>
            <Link
              to="/about"
              className="h-10 flex items-center justify-center rounded-md bg-primary-50 text-sm text-primary-700 cursor-pointer"
            >
              关于我们
            </Link>
          </div>
          <Link
            to="/search"
            className="mt-2 h-11 flex items-center justify-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line"></i>
            </span>
            搜索影视
          </Link>
        </div>
      )}
    </header>
  );
}